<template>
  <div>
    <app-header v-if="staff" :title="originalName ? editTitle : newTitle">
      <template #col-right>
        <b-button
          :disabled="!isFormValid"
          variant="primary"
          class="mr-1"
          @click="save"
        >
          <span v-label-key="'SAVE_CHANGES'">Save Changes</span>
        </b-button>
        <cancel-button />
      </template>
    </app-header>

    <loading-spinner v-if="!staff" />

    <b-container v-if="staff">
      <b-row>
        <b-col md="6">
          <errors v-if="errors.length" :errors="errors" />
          <form ref="editorForm">
            <h3 v-label-key="'EDIT_CORE_INFORMATION_TITLE'">
              Core Information
            </h3>
            <b-form-group :label="`${getLabel('DISTRIBUTOR')} *`">
              <b-form-select
                v-model="staff.distributor_id"
                class="form-control"
                required
                :disabled="distributorLocked"
                @change="refreshStores"
              >
                <b-form-select-option
                  v-if="distributorLocked"
                  :value="staff.distributor_id"
                  disabled
                >
                  <span>{{ staff.distributor_name }}</span>
                </b-form-select-option>

                <template v-else>
                  <b-form-select-option :value="null" disabled>
                    {{ getLabel('PLEASE_SELECT_OPTION') }}
                  </b-form-select-option>
                  <b-form-select-option
                    v-for="distributor in distributors"
                    :key="distributor.id"
                    :value="distributor.id"
                  >
                    {{ distributor.name }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('CHAIN')}`"
              :description="getLabel('EDIT_STAFF_CHAIN_DESCRIPTION') || 'Optionally filter stores by chain'"
            >
              <loading-spinner v-if="storesLoading" />
              <b-form-select v-else v-model="selectedChainId" @change="onChainChange">
                <b-form-select-option :value="null">
                  {{ getLabel('ALL_CHAINS') || '-- All Chains --' }}
                </b-form-select-option>
                <b-form-select-option
                  v-for="chain in chains"
                  :key="chain.id"
                  :value="chain.id"
                >
                  {{ chain.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('STORE')}`"
              :description="getLabel('EDIT_STAFF_STORE_DESCRIPTION')"
            >
              <loading-spinner v-if="storesLoading" />
              <b-form-select v-else v-model="staff.store_id" @change="syncChainFromStore">
                <b-form-select-option key="no_store" :value="null">
                  {{ getLabel('NO_STORE') || '-- No Store --' }}
                </b-form-select-option>
                <b-form-select-option
                  v-for="store in filteredStores"
                  :key="store.id"
                  :value="store.id"
                >
                  {{ store.id }}: {{ store.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('NAME')} *`"
              :description="getLabel('EDIT_STAFF_NAME_DESCRIPTION')"
            >
              <b-form-input v-model="staff.staff_name" type="text" required />
            </b-form-group>

            <br />
            <h3 v-label-key="'STORE_LOGIN_DETAILS_HEADER'">Login Details</h3>
            <b-form-group
              :label="`${getLabel('STAFF_LOGIN_ID')} *`"
              :description="getLabel('EDIT_STAFF_LOGIN_ID_DESCRIPTION')"
            >
              <b-form-input
                v-model="staff.staff_id"
                type="text"
                required
                @blur="validateStaffId()"
                @input="validationStatus = null"
              />
              <div
                v-if="validationStatus === 'valid'"
                class="mt-1 text-success"
              >
                <i class="fas fa-check-circle"></i>
                <span class="ml-1">{{
                  getLabel('STAFF_ID_AVAILABLE') || 'Staff ID is available'
                }}</span>
              </div>
              <div
                v-else-if="validationStatus === 'invalid'"
                class="mt-1 text-danger"
              >
                <i class="fas fa-times-circle"></i>
                <span class="ml-1">{{
                  getLabel('STAFF_ID_NOT_AVAILABLE') ||
                    'Staff ID is not available'
                }}</span>
              </div>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mt-2"
                :disabled="validationStatus === 'loading' || !staff.staff_id"
                @click="validateStaffId"
              >
                <i
                  class="fas fa-sync-alt"
                  :class="{ 'fa-spin': validationStatus === 'loading' }"
                ></i>
                <span class="ml-1">{{
                  getLabel('CHECK_AVAILABILITY') || 'Check Availability'
                }}</span>
              </b-button>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('STAFF_LOGIN_PASSWORD')} *`"
              :description="getLabel('EDIT_STAFF_LOGIN_PASSWORD_DESCRIPTION')"
            >
              <b-input-group>
                <b-form-input
                  v-model="staff.staff_password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="togglePasswordVisibility"
                  >
                    <i
                      :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                    />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </form>
        </b-col>
        <b-col md="7" />
      </b-row>
    </b-container>
  </div>
</template>
<script>
import $auth from '@/services/auth';
import $api from '@/services/api';
import CancelButton from '@/components/CancelButton';
import LoadingSpinner from '@/components/LoadingSpinner';
import Errors from '@/components/Errors';
import AppHeader from '@/components/AppHeader';
import { mapGetters } from 'vuex';

export default {
  components: {
    CancelButton,
    LoadingSpinner,
    Errors,
    AppHeader,
  },
  data() {
    return {
      auth: $auth,
      isEdit: false,
      originalName: null,
      staff: null,
      distributors: null,
      stores: null,
      chains: [],
      selectedChainId: null,
      storesLoading: false,
      errors: [],
      validationStatus: null,
      showPassword: false,
    };
  },

  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
      getReplaceLabel: 'getReplaceLabel',
    }),
    editTitle() {
      return this.getReplaceLabel('EDIT_ITEM_NAME', {
        '{NAME}': this.originalName,
      });
    },
    newTitle() {
      return this.getReplaceLabel('NEW_ITEM_NAME', {
        '{NAME}': this.getLabel('STAFF_MEMBER'),
      });
    },
    distributorLocked() {
      return !!this.isEdit;
    },
    filteredStores() {
      if (!this.stores) return [];
      if (!this.selectedChainId) return this.stores;
      return this.stores.filter(
        (store) => store.chain_id === this.selectedChainId,
      );
    },
    isFormValid() {
      return (
        (this.validationStatus === 'valid' || this.validationStatus === null) &&
        this.staff.staff_id &&
        this.staff.staff_password &&
        this.staff.staff_name &&
        this.staff.distributor_id
      );
    },
  },
  created() {
    this.refreshData();
  },

  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    refreshData() {
      // $api.get('/v1/dashboard/stores').then((stores) => {
      //   this.stores = stores;
      // });

      if (this.$route.query.id) {
        this.isEdit = true;
        $api
          .get(`/v1/dashboard/retailstaff/${this.$route.query.id}`)
          .then((staff) => {
            this.originalName = staff.staff_name;
            this.staff = staff;
            this.refreshStores();
          });
      } else {
        this.staff = {
          store_id: null,
        };
        const queryDistributorId = this.$route.query.distributor_id
          ? parseInt(this.$route.query.distributor_id, 10)
          : null;
        const queryChainId = this.$route.query.chain_id
          ? parseInt(this.$route.query.chain_id, 10)
          : null;
        const querySubdistributorId = this.$route.query.subdistributor_id
          ? parseInt(this.$route.query.subdistributor_id, 10)
          : null;
        const queryStoreId = this.$route.query.store_id
          ? parseInt(this.$route.query.store_id, 10)
          : null;

        // Load distributors first, then apply query param selections
        $api.get('/v1/dashboard/distributors').then((distributors) => {
          this.distributors = distributors.data;

          if (queryDistributorId) {
            this.staff.distributor_id = queryDistributorId;
            this.refreshStores();
          } else if (queryStoreId) {
            $api.get(`/v1/dashboard/stores/${queryStoreId}`).then((store) => {
              this.staff.distributor_id = store.distributor_id;
              this.staff.store_id = queryStoreId;
              this.refreshStores();
            });
          } else if (queryChainId) {
            $api.get('/v1/dashboard/chains').then((chains) => {
              const chain = chains.data.find((c) => c.id === queryChainId);
              if (chain) {
                this.staff.distributor_id = chain.distributor_id;
                this.refreshStores();
              }
            });
          } else if (querySubdistributorId) {
            $api
              .get(`/v1/dashboard/subdistributors/${querySubdistributorId}`)
              .then((sd) => {
                this.staff.distributor_id = sd.data.distributor_id;
                this.refreshStores();
              });
          }
        });
      }
    },
    refreshStores() {
      if (this.staff.distributor_id) {
        this.storesLoading = true;
        this.selectedChainId = null;
        this.chains = [];
        Promise.all([
          $api.get(
            `/v1/dashboard/simplestores?distributor_id=${this.staff.distributor_id}`,
          ),
          $api.get(
            `/v1/dashboard/chains?distributor_id=${this.staff.distributor_id}`,
          ),
        ])
          .then(([stores, chains]) => {
            this.stores = stores.slice().sort((a, b) => a.id - b.id);
            this.chains = chains.data || [];
            this.storesLoading = false;

            if (this.$route.query.chain_id) {
              const chainId = parseInt(this.$route.query.chain_id, 10);
              if (this.chains.some((c) => c.id === chainId)) {
                this.selectedChainId = chainId;
              }
            }
            if (this.$route.query.store_id) {
              const storeId = parseInt(this.$route.query.store_id, 10);
              if (this.stores.some((s) => s.id === storeId)) {
                this.staff.store_id = storeId;
              }
            }

            // Auto-fill chain from current store selection
            this.syncChainFromStore();
          })
          .catch(() => {
            this.storesLoading = false;
          });
      } else {
        this.stores = [];
        this.chains = [];
        this.selectedChainId = null;
      }
    },
    onChainChange() {
      // Reset store selection when chain filter changes
      this.staff.store_id = null;
    },
    syncChainFromStore() {
      if (this.staff.store_id && this.stores) {
        const store = this.stores.find((s) => s.id === this.staff.store_id);
        if (store && store.chain_id) {
          this.selectedChainId = store.chain_id;
        }
      }
    },
    validateStaffId() {
      // Only validate if staff_id has a value
      if (!this.staff.staff_id || !this.staff.staff_id.trim()) {
        return;
      }
      this.validationStatus = 'loading';

      const requestBody = {
        staff_id: this.staff.staff_id,
      };

      // Include the id parameter if it exists (for edit mode)
      if (this.$route.query.id) {
        requestBody.id = this.$route.query.id;
      }

      $api
        .post('/v1/dashboard/retailstaff/validate', requestBody)
        .then((response) => {
          this.validationStatus = null;
          // Handle the validation response
          if (response.unique === false) {
            this.validationStatus = 'invalid';

            // Remove any existing staff_id validation errors first
            this.errors = this.errors.filter(
              (err) => !err.includes('staff ID') && !err.includes('Staff ID'),
            );
            // Add the validation error
            this.errors.push(
              this.getLabel('FORM_ERROR_STAFF_ID_NOT_UNIQUE') ||
                'The staff ID is already in use.',
            );
          } else if (response.unique === true) {
            this.validationStatus = 'valid';
            // Remove staff_id validation errors if validation passes
            this.errors = this.errors.filter(
              (err) => !err.includes('staff ID') && !err.includes('Staff ID'),
            );
          }
        })
        .catch((error) => {
          this.validationStatus = 'invalid';
          console.error('Staff ID validation error:', error);
          // Only show error if it's not due to empty staff_id
          if (this.staff.staff_id && this.staff.staff_id.trim()) {
            this.errors = this.errors.filter(
              (err) => !err.includes('staff ID') && !err.includes('Staff ID'),
            );
            this.errors.push(
              this.getLabel('FORM_ERROR_STAFF_ID_VALIDATION_FAILED') ||
                'Staff ID validation failed. Please try again.',
            );
          }
        });
    },
    validateForm() {
      this.errors = [];
      if (!this.staff.distributor_id) {
        this.errors.push(
          this.getLabel('FORM_ERROR_DISTRIBUTOR_REQUIRED') ||
            'The distributor field is required.',
        );
      }

      if (!this.staff.staff_name) {
        this.errors.push(
          this.getLabel('FORM_ERROR_NAME_REQUIRED') ||
            'The name field is required.',
        );
      }

      if (!this.staff.staff_id) {
        this.errors.push(
          this.getLabel('FORM_ERROR_STAFF_ID_REQUIRED') ||
            'The staff ID field is required.',
        );
      }

      if (!this.staff.staff_password) {
        this.errors.push(
          this.getLabel('FORM_ERROR_STAFF_PASSWORD_REQUIRED') ||
            'The staff password field is required.',
        );
      }
    },
    save() {
      if (!this.$refs.editorForm.checkValidity()) {
        this.$refs.editorForm.reportValidity();
        this.validateForm();
        return;
      }

      this.errors = [];
      if (this.staff.id) {
        $api
          .put(`/v1/dashboard/retailstaff/${this.staff.id}`, this.staff)
          .then((staff) => {
            this.$router.push({
              name: 'staff-detail',
              params: staff,
            });
          })
          .catch((e) => {
            alert(`${this.getLabel('FORM_ERROR_SAVING')}: ${e}`);
          });
      } else {
        this.staff.credits_available = 5;
        $api
          .post('/v1/dashboard/retailstaff', this.staff)
          .then((staff) => {
            this.$router.push({
              name: 'staff-detail',
              params: staff,
            });
          })
          .catch((e) => {
            alert(`${this.getLabel('FORM_ERROR_SAVING')}: ${e}`);
          });
      }
    },
  },
};
</script>
